import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { previewReportAgreementMessage } from "../../../../../api/service/report";
import { SendRecipientPicker, PhoneRecipientShortcuts } from "../../../shared/SendRecipientPicker";
import { FullReportAddress } from "../utils";
import {
  addDays,
  appointmentToFormValues,
  avatarColorForId,
  fmtNo,
  formatAppointmentTimeDetail,
  initials,
} from "./schedule-utils";

function reportDeliveryAddressLabel(report) {
  if (!report) return "—";
  if (
    typeof report.full_delivery_address === "string" &&
    report.full_delivery_address.trim()
  ) {
    return report.full_delivery_address;
  }
  const normalized = report.delivery_address
    ? report
    : report.deliveryAddress
      ? { ...report, delivery_address: report.deliveryAddress }
      : report;
  const formatted = FullReportAddress(normalized);
  if (typeof formatted === "string" && formatted.trim()) return formatted;
  return "—";
}

export default function ReportAgreementNotifPanel({
  open,
  embedded = false,
  employee,
  appointment,
  weekStart,
  report,
  fixedTime,
  requireConfirmation,
  sendSms,
  setSendSms,
  sendEmail,
  setSendEmail,
  sendAgreementToCustomer,
  setSendAgreementToCustomer,
  phone,
  setPhone,
  email,
  setEmail,
  setRecipientDraft,
  onReceiverPick,
  onBack,
}) {
  const { t } = useTranslation();
  const [smsPreview, setSmsPreview] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const dateLabel = appointment ? fmtNo(addDays(weekStart, appointment.day)) : "—";
  const timeLabel = appointment
    ? formatAppointmentTimeDetail(appointment, fixedTime)
    : "—";

  const customerName =
    report?.customer?.name || report?.customer?.displayTitle || "—";
  const customerAddress = reportDeliveryAddressLabel(report);

  const previewPayload = useMemo(() => {
    if (!report?.id || !appointment) return null;
    const formTimes = appointmentToFormValues(appointment, weekStart, fixedTime);
    if (!formTimes?.agreement_date) return null;
    return {
      id: report.id,
      fixedTime,
      appointmentConfirmationRequired: requireConfirmation,
      sendEmailToCustomer: sendEmail,
      sendSmsToCustomer: sendSms,
      ...formTimes,
    };
  }, [
    report?.id,
    appointment,
    weekStart,
    fixedTime,
    requireConfirmation,
    sendEmail,
    sendSms,
  ]);

  useEffect(() => {
    if (!open || !previewPayload || !sendAgreementToCustomer || !sendSms) {
      setSmsPreview("");
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);

    previewReportAgreementMessage(previewPayload)
      .then((res) => {
        if (cancelled) return;
        setSmsPreview(res?.data?.data?.sms || res?.data?.sms || "");
      })
      .catch(() => {
        if (!cancelled) setSmsPreview("");
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, previewPayload, sendAgreementToCustomer, sendSms]);

  const receivers = [
    report?.customer?.email || report?.customer?.mobile
      ? { key: "customer", label: t("Customer"), email: report.customer.email, mobile: report.customer.mobile }
      : null,
    report?.contact?.email || report?.contact?.mobile
      ? { key: "contact", label: t("Contact"), email: report.contact.email, mobile: report.contact.mobile }
      : null,
    report?.project?.contact?.email || report?.project?.contact?.mobile
      ? { key: "project", label: t("Project Contact"), email: report.project.contact.email, mobile: report.project.contact.mobile }
      : null,
    report?.agreement?.contact?.email || report?.agreement?.contact?.mobile
      ? { key: "agreement", label: t("Service Agreement Contact"), email: report.agreement.contact.email, mobile: report.agreement.contact.mobile }
      : null,
  ].filter(Boolean);

  return (
    <div className={`ra-notif-panel${open ? " open" : ""}${embedded ? " embedded" : ""}`}>
      {!embedded && (
        <div className="ra-notif-head">
          <div>
            <h3>{t("report.agreement.section.notify")}</h3>
            <p>{t("report.agreement.section.notify_help")}</p>
          </div>
          <button type="button" className="ra-close-x" onClick={onBack} aria-label={t("Back")}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M11 3L5 8l6 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )}

      <div className="ra-notif-body">
        {employee && appointment && (
          <div className="ra-summary-card">
            <div className="who-row">
              <div
                className="ra-emp-avatar"
                style={{ background: avatarColorForId(employee.id) }}
              >
                {initials(employee.displayTitle || employee.name)}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>
                  {employee.displayTitle || employee.name}
                </div>
              </div>
            </div>
            <div className="when">
              <span className="k">{t("Agreement date")}</span>
              <span className="v">{dateLabel}</span>
              <span className="k">{fixedTime ? t("Fixed time") : t("report.agreement.time_range")}</span>
              <span className="v">{timeLabel}</span>
              <span className="k">{t("Customer")}</span>
              <span className="v">{customerName}</span>
              <span className="k">{t("Address")}</span>
              <span className="v">{customerAddress}</span>
            </div>
          </div>
        )}

        <div>
          <div className="ra-section-label" style={{ marginBottom: 8 }}>
            {t("report.agreement.send_to_customer")}
          </div>
          <div className="ra-toggle-row" style={{ paddingTop: 0 }}>
            <div className="grow">
              <div className="sub">
                {sendAgreementToCustomer
                  ? t("report.agreement.send_to_customer_hint_on")
                  : t("report.agreement.send_to_customer_hint_off")}
              </div>
            </div>
            <button
              type="button"
              className={`ra-tog${sendAgreementToCustomer ? " on" : ""}`}
              onClick={() => { setRecipientDraft?.(""); setSendAgreementToCustomer(!sendAgreementToCustomer); }}
              aria-pressed={sendAgreementToCustomer}
            />
          </div>
        </div>

        {sendAgreementToCustomer && (
          <>
            {sendSms && <PhoneRecipientShortcuts label={t("Phone number")} options={receivers} onSelect={(receiver) => onReceiverPick(receiver.key)} />}

            <div>
              <div className="ra-section-label" style={{ marginBottom: 8 }}>
                {t("report.agreement.redesign.channels")}
              </div>
              <div className="ra-send-row">
                <button
                  type="button"
                  className={`ra-send-chip${sendSms ? " on" : ""}`}
                  onClick={() => setSendSms(!sendSms)}
                >
                  <span style={{ fontSize: 18 }} aria-hidden="true">
                    💬
                  </span>
                  <div className="lbl">{t("send-sms.customer")}</div>
                  <div className={`ra-tog${sendSms ? " on" : ""}`} style={{ marginLeft: "auto" }} />
                </button>
                <button
                  type="button"
                  className={`ra-send-chip${sendEmail ? " on" : ""}`}
                  onClick={() => { setRecipientDraft?.(""); setSendEmail(!sendEmail); }}
                >
                  <span style={{ fontSize: 18 }} aria-hidden="true">
                    ✉️
                  </span>
                  <div className="lbl">{t("Send email to customer")}</div>
                  <div className={`ra-tog${sendEmail ? " on" : ""}`} style={{ marginLeft: "auto" }} />
                </button>
              </div>
            </div>

            {sendSms && (
              <div className="ra-field">
                <label htmlFor="ra-notif-phone">{t("Phone number")}</label>
                <input
                  id="ra-notif-phone"
                  value={phone || ""}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            )}
            {sendEmail && (
              <SendRecipientPicker
                multiple={false} customerId={report?.customer?.id}
                options={receivers.map((receiver) => ({ ...receiver, name: receiver.label }))}
                value={email ? [{ email }] : []}
                onChange={(rows) => setEmail(rows[0]?.email || "")}
                onDraftChange={setRecipientDraft}
              />
            )}

            {sendSms && (
              <div>
                <div className="ra-section-label" style={{ marginBottom: 8 }}>
                  {t("report.agreement.redesign.preview")}
                </div>
                <div
                  style={{
                    background: "#f1f5f9",
                    borderRadius: 10,
                    padding: 12,
                    fontSize: 12,
                    lineHeight: 1.5,
                    color: "#334155",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 6, color: "#0f172a" }}>
                    SMS
                  </div>
                  <div>
                    {previewLoading
                      ? t("Loading...")
                      : smsPreview || t("report.agreement.redesign.preview_empty")}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
